import { useContext } from 'react';
import { ToastContext } from '../../components/toast/context/toast-context';
import { ToastMessage } from 'primereact/toast';

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  const { toastRef, getToastMessage, setToastMessage, deleteToastMessage, clearToastMap } = context;

  const messageTypeOptions = {
    successMessage: {
      severity: 'success',
      life: 3000,
      summary: 'Success',
    },

    errorMessage: {
      severity: 'error',
      life: 4000,
      summary: 'Error',
    },

    infoMessage: {
      severity: 'info',
      life: 3000,
      summary: 'Info',
    },
  } satisfies Record<string, ToastMessage>;

  type ToastMessageType = keyof typeof messageTypeOptions;

  const showToastMessage = (
    messageType: ToastMessageType,
    contentConstructor?: (id: string) => JSX.Element
  ) => {
    const id = crypto.randomUUID();

    const message: ToastMessage = {
      ...messageTypeOptions[messageType],
      id: id,
      content: contentConstructor ? contentConstructor(id) : '',
    };

    setToastMessage(id, message);
    toastRef.current?.show(message);
  };

  const removeToastMessage = (id: string) => {
    const message = getToastMessage(id);

    if (!message) {
      throw new Error(`Toast message with id "${id}" not found`);
    }

    toastRef.current?.remove(message);
    deleteToastMessage(id);
  };

  const clearAllToastMessages = () => {
    toastRef.current?.clear();
    clearToastMap();
  };

  return {
    showToastMessage,
    removeToastMessage,
    clearAllToastMessages,
  };
};
